import JobApplication from './JobApplication'
import JobListItem from './JobListItem'
import BigNumber from 'bignumber.js'
import cloneDeep from 'lodash/cloneDeep'
import unescape from 'lodash/unescape'
import { FileCategories } from '@/constants/backend/FileCategories'
import { ModerationStages } from '@/constants/backend/ModerationStages'
import { Blockchain } from '@/constants/blockchain'
import { BACKEND_CURRENCY_USDT_ID, CURRENCY_FIELD_BACKEND_ID } from '@/constants/currency'
import { JobOfferStages } from '@/constants/job/jobOfferStages'
import { JobStage, STAGE_NEW, STAGE_STARTED } from '@/constants/job/jobStages'
import { JobStatus, STATUS_DRAFT, STATUS_NEW, STATUS_PUBLISHED } from '@/constants/job/jobStatuses'
import { AccountTypes } from '@/constants/user/accountTypes'
import { CustomerTypes } from '@/constants/user/roles'
import { DATE_TIME_FORMAT } from '@/constants/utils/date'
import File from '@/models-ts/File'
import Skill from '@/models-ts/Skill'
import Avatar from '@/models-ts/user/Avatar'
import { getCurrency } from '@/utils-ts/currencies'
import { parseSlug } from '@/utils-ts/parser'
import { addDays, convertToLocal, convertToUTC } from '@/utils/date'
import { getUrl } from '@/utils/file'
import { formatCurrency, formatUsd } from '@/utils/moneyFormat'
import GigListItem from '../gigs/GigListItem'

export default class Job {
  id: number
  contractVersion: number | null
  slug: string
  sc_id: string
  created_at: string
  published_at: string
  edited_at: string
  name: string
  description: string
  blockchain: Blockchain
  budget: string
  delivery_time_at?: string | null
  currency: number | null
  estimate: number | null
  inProgressAt: string | null
  escrow_balance: string | null
  customer_wallet: string | null
  freelancer_wallet: string | null
  customer_id: number
  freelancer_id: number | null
  freelancer: {
    id: number
    name: string
    avatar: Avatar
    avgReviews: number | null
    reviewsCount: number
    type: AccountTypes
  } | null
  moderation_stage?: ModerationStages | null
  relations: {
    File: Array<File>
    Skill: Array<Skill>
    Customer: {
      id: number
      name: string
      avatar: Avatar
      avgReviews: number | null
      website: string | null
      type: AccountTypes
      reviewsCount: number
      is_customer_verified: boolean
      customerType: CustomerTypes
    } | null
    Review: Array<{
      id: number
      fromClient: boolean
      created_at: string
      text: string | null
      rates: {}
      user: {
        id: number
        name: string
        avatar: Avatar | null
        type: number
      }
    }>
  }
  applications: Array<JobApplication>
  status: JobStatus
  stage: JobStage
  more: {
    byCategory: Array<JobListItem>
    byUser: Array<JobListItem>
    gigsByCategory: Array<GigListItem>
  }
  isDone: boolean
  meta: {}

  constructor(data: Partial<Job> | void) {
    Object.assign(
      this,
      cloneDeep({
        id: 0,
        contractVersion: 2,
        slug: '',
        sc_id: null,
        created_at: '',
        published_at: '',
        edited_at: '',
        name: '',
        description: '',
        blockchain: Blockchain.Ethereum,
        budget: '0.00',
        currency: BACKEND_CURRENCY_USDT_ID,
        estimate: 0,
        inProgressAt: null,
        escrow_balance: null,
        customer_wallet: null,
        freelancer_wallet: null,
        delivery_time_at: null,
        customer_id: 0,
        freelancer_id: null,
        relations: {
          File: [],
          Skill: [],
          Offer: [],
          Customer: null,
          Review: [],
        },
        applications: [],
        status: STATUS_NEW,
        stage: STAGE_NEW,
        more: {
          byCategory: [],
          byUser: [],
          gigsByCategory: [],
        },
        meta: {},
        ...data,
      })
    )
  }

  static fromServer(data: LX.Job) {
    const customerData = data.relations.Customer
    let customer = null
    if (customerData) {
      customer = {
        id: customerData.id,
        name: unescape(customerData.name),
        avatar: Avatar.fromServer(customerData.avatar),
        avgReviews: customerData.profile?.avg_reviews || 0,
        reviewsCount: customerData.reviews_count || 0,
        website:
          (customerData.profile.type === CustomerTypes.COMPANY
            ? customerData.profile.company_website
            : customerData.profile.individual_website) || '',
        type: customerData.type,
        is_customer_verified: Number(customerData.is_customer_verified) === 1,
        customerType: customerData.profile?.type,
      }
    }
    const freelancerData = data.relations.Freelancer
    let freelancer: Job['freelancer'] = null
    if (freelancerData && !Array.isArray(freelancerData)) {
      freelancer = {
        id: freelancerData?.id,
        name: unescape(freelancerData?.name),
        avatar: Avatar.fromServer(freelancerData.avatar),
        type: freelancerData.type,
        avgReviews: freelancerData.profile.avg_reviews,
        reviewsCount: freelancerData.reviews_count,
      }
    }

    return new Job({
      id: data.id,
      contractVersion: data.contract_version,
      slug: parseSlug(data.slug),
      sc_id: data.sc_id,
      created_at: data.created_at,
      published_at: data.updated_at,
      edited_at: data.edited_at || data.created_at,
      name: unescape(data.name),
      description: data.description,
      delivery_time_at: data.delivery_time_at ? convertToLocal(data.delivery_time_at) : null,
      blockchain: data.blockchain,
      budget: data.budget ? String(Number(data.budget).toFixed(2)) : '0.00',
      currency: data.currency,
      estimate: data.deadline ? data.deadline / 86400 : 0, // from seconds to day
      inProgressAt: data.in_progress_at ? convertToLocal(data.in_progress_at) : null,
      escrow_balance: data.escrow_balance,
      customer_wallet: data.customer_wallet,
      freelancer_wallet: data.freelancer_wallet,
      customer_id: data.customer_id,
      freelancer_id: data.freelancer_id,
      freelancer: freelancer,
      status: data.status,
      stage: data.stage,
      meta: data.meta,
      more: {
        byCategory: (data.more?.jobs?.by_category || []).map(JobListItem.fromMoreServer),
        byUser: (data.more?.jobs?.by_user || []).map(JobListItem.fromMoreServer),
        gigsByCategory: (data.more?.gigs?.by_category || []).map(GigListItem.fromServer),
      },
      moderation_stage: data.moderation_stage,
      relations: {
        File: (data.relations?.File || []).map(File.fromServer),
        Skill: (data.relations.Skill || []).map(Skill.fromServer),
        Customer: customer,
        // TODO change Review when rewrite Freelance model to ts
        Review: (data.relations.Review || []).map((review: LX.Review) => {
          const result = {
            id: review.id,
            created_at: review.created_at,
            text: review.text || null,
            fromClient: false,
            user: {
              id: 0,
              name: '',
              avatar: new Avatar(),
              type: 0 as AccountTypes,
            },
            rates: {},
          }
          if (review.from_user_id === data.relations?.Customer?.id) {
            result.fromClient = true
            result.user.id = data.relations.Customer?.id
            result.user.type = data.relations.Customer?.type
            result.user.name = unescape(data.relations.Customer?.name)
            result.user.avatar = data.relations.Customer?.avatar ? Avatar.fromServer(data.relations.Customer?.avatar) : new Avatar()
          } else {
            result.fromClient = false
            // const freelancer = { ...(data.relations.Offer || [])
            //   .map(offer => offer.relations.Freelancer)
            //   .find(freelancer => freelancer.id === review.from_user_id)
            // }
            result.user.id = freelancer?.id || 0
            result.user.type = freelancer?.type || 0
            result.user.name = unescape(freelancer?.name)
            result.user.avatar = freelancer?.avatar || new Avatar()
          }
          result.rates = (review.relations?.Rate || []).reduce((res: any, rate: any) => {
            res[rate.category] = rate.rate
            return res
          }, {})
          return result
        }),
      },
      applications: (data.relations.Application || []).map((app) => {
        const appFreelancer = app.relations.Freelancer!
        const customer = data.relations.Customer!
        return JobApplication.fromServer({
          id: app.id,
          status: app.status,
          is_read: app.is_read,
          comment: app.comment,
          budget: app.budget,
          deadline: app.deadline,
          tabs_meta: app.tabs_meta,
          created_at: app.created_at,
          updated_at: app.updated_at,
          relations: {
            Offers: (data.relations.Offer || []).filter((offer) => offer.job_application_id === app.id),
            Job: {
              id: data.id,
              slug: data.slug,
              name: data.name,
              stage: data.stage,
              status: data.status,
              budget: data.budget ? String(Number(data.budget).toFixed(2)) : '0.00',
              freelancer_id: data.freelancer_id,
              delivery_time_at: data.delivery_time_at ? convertToLocal(data.delivery_time_at) : null,
              blockchain: data.blockchain,
              currency: data.currency,
              escrow_balance: data.escrow_balance,
              deadline: data.deadline,
              in_progress_at: data.in_progress_at,
              is_done: data.is_done,
              contract_version: data.contract_version,
              relations: {
                File: data.relations.File,
                Review: [],
              },
            },
            Freelancer: {
              id: appFreelancer.id,
              name: appFreelancer.name,
              type: appFreelancer.type,
              avatar: appFreelancer.avatar,
              profile: {
                avg_reviews: appFreelancer.profile.avg_reviews,
                avg_reputation: appFreelancer.profile.avg_reputation,
              },
              reviews_count: appFreelancer.reviews_count,
            },
            Customer: {
              id: customer.id,
              name: customer.name,
              type: customer.type,
              avatar: customer.avatar,
              is_customer_verified: customer.is_customer_verified,
              profile: {
                avg_reviews: customer.profile?.avg_reviews || 0,
                type: customer.profile.type,
                company_website: customer.profile.company_website,
                individual_website: customer.profile.individual_website,
              },
              reviews_count: customer.reviews_count,
            },
          },
        })
      }),
      isDone: Boolean(data.is_done || false),
    })
  }

  toServer(): LX.CreateJobPayload {
    return {
      name: this.name.trim(),
      description: this.description,
      currency: this.currency,
      budget: new BigNumber(this.budget).toNumber(),
      blockchain: this.blockchain,
      delivery_time_at: this.delivery_time_at ? convertToUTC(this.delivery_time_at, DATE_TIME_FORMAT) : null,
      relations: {
        Skill: this.relations.Skill.map(({ id }) => ({ id })),
        File: this.relations.File,
      },
    }
  }

  get isDraft() {
    return this.status === STATUS_DRAFT
  }

  get isPublished() {
    return this.status === STATUS_PUBLISHED
  }

  get isNotStarted() {
    return this.stage === STAGE_NEW
  }

  get wasStarted() {
    return ![STAGE_NEW, STAGE_STARTED].includes(this.stage)
  }

  get selectedCurrency() {
    return getCurrency({ value: this.currency, blockchain: this.blockchain, field: CURRENCY_FIELD_BACKEND_ID })
  }

  // get selectedOffer () {
  //   return (this.relations?.Offer || []).find(offer => offer.stage === OFFER_ACCEPTED)
  // }

  get deadline() {
    const inProgressAt = this.inProgressAt || ''
    const estimate = this.estimate || 0
    return addDays(inProgressAt, estimate)
  }

  get formattedBudget() {
    if (this.isNotStarted) {
      return `$${formatUsd(this.budget)}`
    }
    const currency = this.selectedCurrency!
    const escrowBalance = new BigNumber(this.escrow_balance || 0).dividedBy(currency.baseUnits)
    return `${formatCurrency(escrowBalance, { currency })} ${currency.name}`
  }

  get document() {
    const pdf = this.relations.File.find((file: any) => file.category === FileCategories.CAT_GENERATED_PDF)
    return pdf && getUrl(pdf)
  }

  get hasAcceptedOffer() {
    return this.applications.some((application: JobApplication) => application?.offer?.stage === JobOfferStages.ACCEPTED_BY_CUSTOMER)
  }
}
